'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Search, Loader2, Check, Plus, ClipboardPaste, UserPlus, AlertCircle, CheckCircle2, Youtube } from 'lucide-react'
import { subscribe, mergeSubscriptions, useSubscriptions } from '@/lib/subscriptions'
import { parseSubscriptionsPasteDetailed, type PastedChannel } from '@/lib/parse-yt-subscriptions'
import { Button, Chip, Input, cx } from '@/components/ui/kit'

/**
 * Fenêtre « Ajouter des abonnements » (feuille plein-largeur sur mobile,
 * fenêtre centrée sur desktop, compatible mode TV : [data-tvmodal], Échap /
 * Retour ferment, tout est focusable au clavier/télécommande).
 *
 * Deux onglets :
 *   - « Par nom » : on tape un nom de chaîne/youtubeur → /api/youtube/channel-search
 *     → clic sur « S'abonner » = ajout immédiat (subscribe()).
 *   - « Coller depuis YouTube » : l'utilisateur colle le texte de
 *     youtube.com/feed/channels (Tout sélectionner + Copier) → on extrait les
 *     noms (parseSubscriptionsPasteDetailed), il décoche ce qu'il ne veut pas,
 *     puis « Tout ajouter » résout chaque nom/@handle (3 à la fois) et
 *     s'abonne au meilleur résultat (mergeSubscriptions, au fil de l'eau).
 */

export type AddTab = 'name' | 'paste'
type Found = { channelId: string; title: string; thumbnail: string; subscribers?: number; handle?: string }

const nf = typeof Intl !== 'undefined' ? new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 }) : null
function fmtSubs(n?: number): string {
  if (!n) return ''
  return `${nf ? nf.format(n) : n} d'abonné${n > 1 ? 's' : ''}`
}
const normTitle = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
const keyOf = (c: PastedChannel) => (c.handle || c.name).toLowerCase()

async function searchChannels(q: string, max: number, signal?: AbortSignal): Promise<Found[]> {
  const r = await fetch(`/api/youtube/channel-search?q=${encodeURIComponent(q)}&max=${max}`, { signal })
  const j = await r.json().catch(() => null)
  if (!r.ok && !j?.items?.length) throw new Error(j?.error || 'Recherche indisponible')
  return Array.isArray(j?.items) ? j.items : []
}

/** Meilleure correspondance : @handle exact > titre identique (sans accents/espaces) > 1er résultat. */
function bestMatch(c: PastedChannel, items: Found[]): Found | null {
  if (!items.length) return null
  if (c.handle) {
    const h = c.handle.toLowerCase()
    const byHandle = items.find(i => (i.handle || '').toLowerCase() === h)
    if (byHandle) return byHandle
  }
  const n = normTitle(c.name.replace(/^@/, ''))
  return items.find(i => normTitle(i.title) === n) || items[0]
}

export default function AddSubscriptions({ initialTab = 'name', onClose }: { initialTab?: AddTab; onClose: () => void }) {
  const [tab, setTab] = useState<AddTab>(initialTab)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const busyRef = useRef(false)

  // Fermeture : Échap (clavier/TV) et Retour logique TV ('blc-tv-back').
  // Backspace ferme aussi (télécommandes) SAUF quand on tape dans un champ.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      if (e.key === 'Escape' || e.keyCode === 27 || (e.key === 'Backspace' && !typing)) {
        e.preventDefault(); e.stopPropagation(); onCloseRef.current()
      }
    }
    const onBack = (e: Event) => { if ((e as any).cancelable) e.preventDefault(); onCloseRef.current() }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('blc-tv-back', onBack as EventListener)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('blc-tv-back', onBack as EventListener)
      document.body.style.overflow = prev
    }
  }, [])

  return (
    <div onClick={() => { if (!busyRef.current) onClose() }}
      className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Ajouter des abonnements" data-tvmodal="1"
        className="w-full sm:max-w-xl max-h-[92dvh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-spanel border border-white/[0.09] shadow-[0_24px_60px_rgba(0,0,0,.6)] relative">
        {/* En-tête */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <span className="w-10 h-10 shrink-0 rounded-2xl grid place-items-center bg-gradient-to-br from-a1 to-a2 shadow-[0_0_18px_rgb(var(--a2)/.45)]">
            <UserPlus size={19} className="text-white" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[18px] font-extrabold tracking-tight leading-tight">Ajouter des abonnements</h2>
            <p className="text-[12.5px] text-white/50">Par nom ou en collant ta liste YouTube</p>
          </div>
          <button type="button" onClick={onClose} title="Fermer" aria-label="Fermer"
            className="w-9 h-9 shrink-0 rounded-full bg-white/[0.07] hover:bg-white/[0.14] focus-visible:ring-2 focus-visible:ring-a2 grid place-items-center text-white/70 hover:text-white transition">
            <X size={17} />
          </button>
        </div>

        {/* Onglets */}
        <div role="tablist" className="flex gap-2 px-5 pb-3">
          <Chip active={tab === 'name'} onClick={() => setTab('name')} className="!px-4 !py-2">
            <span className="inline-flex items-center gap-1.5"><Search size={14} /> Par nom</span>
          </Chip>
          <Chip active={tab === 'paste'} onClick={() => setTab('paste')} className="!px-4 !py-2">
            <span className="inline-flex items-center gap-1.5"><ClipboardPaste size={14} /> Coller depuis YouTube</span>
          </Chip>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {tab === 'name' ? <ByName /> : <FromPaste busyRef={busyRef} />}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ Par nom */

function ByName() {
  const subs = useSubscriptions()
  const have = useMemo(() => new Set(subs.map(s => s.channelId)), [subs])
  const [q, setQ] = useState('')
  const [items, setItems] = useState<Found[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Recherche avec anti-rebond (450 ms) : chaque recherche par nom coûte cher en quota.
  useEffect(() => {
    const s = q.trim()
    if (s.length < 2) { setItems([]); setError(''); setLoading(false); return }
    const ctrl = new AbortController()
    setLoading(true)
    const t = setTimeout(() => {
      searchChannels(s, 6, ctrl.signal)
        .then(r => { setItems(r); setError('') })
        .catch(e => { if (e?.name !== 'AbortError') { setItems([]); setError(e?.message || 'Recherche indisponible') } })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    }, 450)
    return () => { clearTimeout(t); ctrl.abort() }
  }, [q])

  return (
    <div>
      <form onSubmit={e => e.preventDefault()}>
        <Input icon={Search} value={q}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
          autoFocus enterKeyHint="search" aria-label="Nom de la chaîne"
          placeholder="Nom d'une chaîne ou @handle…" />
      </form>

      <div className="mt-4 min-h-[120px]">
        {loading && (
          <div className="flex items-center gap-2 text-[14px] text-white/55 py-6 justify-center">
            <Loader2 size={17} className="animate-spin text-a3" /> Recherche des chaînes…
          </div>
        )}
        {!loading && error && (
          <p className="flex items-center gap-2 text-[14px] text-rose-300 py-4"><AlertCircle size={16} /> {error}</p>
        )}
        {!loading && !error && q.trim().length >= 2 && items.length === 0 && (
          <p className="text-[14px] text-white/50 py-6 text-center">Aucune chaîne trouvée pour « {q.trim()} ».</p>
        )}
        {!loading && q.trim().length < 2 && (
          <p className="text-[13.5px] text-white/45 py-6 text-center">Tape le nom d'un youtubeur (ex. « Squeezie ») ou son @handle.</p>
        )}
        {!loading && items.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {items.map(it => {
              const on = have.has(it.channelId)
              return (
                <li key={it.channelId} className="flex items-center gap-3 rounded-2xl px-2.5 py-2 hover:bg-white/[0.05] transition">
                  <ChannelAvatar src={it.thumbnail} title={it.title} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold truncate">{it.title}</div>
                    <div className="text-[12.5px] text-white/50 truncate">
                      {[it.handle, fmtSubs(it.subscribers)].filter(Boolean).join(' • ') || 'Chaîne YouTube'}
                    </div>
                  </div>
                  {on ? (
                    <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-semibold text-a4 bg-a2/15">
                      <Check size={15} /> Abonné
                    </span>
                  ) : (
                    <Button icon={Plus} className="!px-4 !py-2 !text-[13px] shrink-0"
                      onClick={() => subscribe({ channelId: it.channelId, title: it.title, thumbnail: it.thumbnail })}>
                      S'abonner
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function ChannelAvatar({ src, title, size = 44 }: { src?: string; title: string; size?: number }) {
  const [err, setErr] = useState(false)
  return src && !err ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt="" width={size} height={size} loading="lazy" referrerPolicy="no-referrer" onError={() => setErr(true)}
      className="shrink-0 rounded-full object-cover bg-white/[0.06]" style={{ width: size, height: size }} />
  ) : (
    <span className="shrink-0 rounded-full grid place-items-center bg-gradient-to-br from-a1 to-adeep text-white font-bold" style={{ width: size, height: size }}>
      {(title || '?').replace(/^@/, '').charAt(0).toUpperCase()}
    </span>
  )
}

/* ------------------------------------------------ Coller depuis YouTube */

type RunState = { done: number; total: number; added: number; already: number; notFound: string[]; failed: number; finished: boolean }

function FromPaste({ busyRef }: { busyRef: React.MutableRefObject<boolean> }) {
  const subs = useSubscriptions()
  const [text, setText] = useState('')
  const [off, setOff] = useState<Set<string>>(new Set())   // clés DÉcochées (tout coché par défaut)
  const [run, setRun] = useState<RunState | null>(null)
  const cancelRef = useRef(false)
  const areaRef = useRef<HTMLTextAreaElement | null>(null)

  const parsed = useMemo(() => parseSubscriptionsPasteDetailed(text), [text])
  const picked = useMemo(() => parsed.filter(c => !off.has(keyOf(c))), [parsed, off])
  const running = !!run && !run.finished

  useEffect(() => { busyRef.current = running }, [running, busyRef])
  // Fermeture de la fenêtre pendant l'import → on arrête proprement.
  useEffect(() => () => { cancelRef.current = true; busyRef.current = false }, [busyRef])
  useEffect(() => {
    const t = setTimeout(() => { try { areaRef.current?.focus() } catch {} }, 60)
    return () => clearTimeout(t)
  }, [])

  const toggle = (c: PastedChannel) => setOff(prev => {
    const n = new Set(prev); const k = keyOf(c)
    if (n.has(k)) n.delete(k); else n.add(k)
    return n
  })

  const pasteFromClipboard = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) { setText(t); setOff(new Set()); setRun(null) } } catch {}
  }

  const addAll = async () => {
    const list = picked
    if (!list.length || running) return
    cancelRef.current = false
    const already0 = new Set(subs.map(s => s.channelId))
    const st: RunState = { done: 0, total: list.length, added: 0, already: 0, notFound: [], failed: 0, finished: false }
    setRun({ ...st })
    let next = 0
    // 3 résolutions à la fois maximum (ménage l'API / Invidious).
    const worker = async () => {
      while (!cancelRef.current && next < list.length) {
        const c = list[next++]
        try {
          const items = await searchChannels(c.handle || c.name, c.handle ? 3 : 5)
          const m = bestMatch(c, items)
          if (!m) st.notFound.push(c.name)
          else if (already0.has(m.channelId)) st.already++
          else {
            // Ajout au fil de l'eau → la liste des abonnements se met à jour en direct.
            const n = mergeSubscriptions([{ channelId: m.channelId, title: m.title, thumbnail: m.thumbnail }])
            if (n) st.added++; else st.already++
            already0.add(m.channelId)
          }
        } catch {
          st.failed++; st.notFound.push(c.name)
        }
        st.done++
        if (!cancelRef.current) setRun({ ...st, notFound: [...st.notFound] })
      }
    }
    await Promise.all([worker(), worker(), worker()])
    if (!cancelRef.current) setRun({ ...st, notFound: [...st.notFound], finished: true })
  }

  const pct = run ? Math.round((run.done / Math.max(1, run.total)) * 100) : 0

  return (
    <div>
      <ol className="text-[13px] text-white/60 leading-relaxed list-decimal pl-5 mb-3 space-y-0.5">
        <li>Ouvre <a href="https://www.youtube.com/feed/channels" target="_blank" rel="noopener noreferrer" className="text-a4 hover:text-white underline underline-offset-2 inline-flex items-center gap-1"><Youtube size={13} /> youtube.com/feed/channels</a> (fais défiler jusqu'en bas).</li>
        <li><b className="text-white/80">Ctrl + A</b> puis <b className="text-white/80">Ctrl + C</b> (tout sélectionner, copier).</li>
        <li>Colle ici : on ne garde que les noms des chaînes.</li>
      </ol>

      <textarea ref={areaRef} value={text} disabled={running}
        onChange={e => { setText(e.target.value); setOff(new Set()); setRun(null) }}
        placeholder="Coller mes abonnements YouTube…" aria-label="Coller mes abonnements YouTube"
        rows={5}
        className="lg-field w-full rounded-xl bg-white/[0.06] border border-white/[0.09] text-white text-[13.5px] p-3.5 placeholder-smuted focus:outline-none focus:border-a2/70 focus:bg-white/[0.08] transition resize-y min-h-[110px] max-h-[40dvh] disabled:opacity-60" />

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Button variant="secondary" icon={ClipboardPaste} onClick={pasteFromClipboard} disabled={running} className="!px-4 !py-2 !text-[13px]">
          Coller le presse-papiers
        </Button>
        {text && !running && (
          <button type="button" onClick={() => { setText(''); setOff(new Set()); setRun(null) }}
            className="text-[13px] font-semibold text-white/50 hover:text-white px-2 py-1 rounded-full focus-visible:ring-2 focus-visible:ring-a2 transition">
            Effacer
          </button>
        )}
      </div>

      {text.trim() && parsed.length === 0 && (
        <p className="mt-4 flex items-center gap-2 text-[13.5px] text-white/55"><AlertCircle size={15} className="text-a3" /> Aucune chaîne reconnue dans ce texte.</p>
      )}

      {parsed.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2.5">
            <h3 className="text-[14.5px] font-bold">
              {parsed.length} chaîne{parsed.length > 1 ? 's' : ''} détectée{parsed.length > 1 ? 's' : ''}
              <span className="text-white/45 font-semibold"> · {picked.length} sélectionnée{picked.length > 1 ? 's' : ''}</span>
            </h3>
            <span className="flex-1" />
            {!running && (
              <button type="button" onClick={() => setOff(picked.length === parsed.length ? new Set(parsed.map(keyOf)) : new Set())}
                className="text-[12.5px] font-semibold text-a4 hover:text-white px-2 py-1 rounded-full focus-visible:ring-2 focus-visible:ring-a2 transition">
                {picked.length === parsed.length ? 'Tout décocher' : 'Tout cocher'}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-[30dvh] overflow-y-auto pr-1">
            {parsed.map(c => {
              const on = !off.has(keyOf(c))
              return (
                <Chip key={keyOf(c)} active={on} onClick={() => { if (!running) toggle(c) }} className="!py-1.5 max-w-full">
                  <span className="inline-flex items-center gap-1.5 max-w-full" title={c.handle && c.handle !== c.name ? `${c.name} (${c.handle})` : c.name}>
                    <span className={cx('w-4 h-4 shrink-0 rounded-[5px] grid place-items-center border', on ? 'bg-white/25 border-white/50' : 'border-white/25')}>
                      {on && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="truncate">{c.name}</span>
                  </span>
                </Chip>
              )
            })}
          </div>

          {/* Progression / résumé */}
          {run && (
            <div className="mt-4 rounded-2xl bg-white/[0.04] border border-white/[0.08] p-3.5" aria-live="polite">
              <div className="flex items-center gap-2 text-[13.5px] font-semibold">
                {run.finished ? <CheckCircle2 size={16} className="text-a3" /> : <Loader2 size={16} className="animate-spin text-a3" />}
                {run.finished ? 'Import terminé' : <>Ajout en cours… <span className="tabular-nums text-white/60">{run.done}/{run.total}</span></>}
              </div>
              <div className="mt-2.5 h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-a1 to-a2 transition-[width] duration-300" style={{ width: `${pct}%` }} />
              </div>
              {run.finished && (
                <div className="mt-3 text-[13px] text-white/65 space-y-1">
                  <p><b className="text-white tabular-nums">{run.added}</b> ajouté{run.added > 1 ? 's' : ''}
                    {run.already > 0 && <> · <span className="tabular-nums">{run.already}</span> déjà suivi{run.already > 1 ? 's' : ''}</>}
                    {run.notFound.length > 0 && <> · <span className="tabular-nums text-rose-300">{run.notFound.length}</span> introuvable{run.notFound.length > 1 ? 's' : ''}</>}
                  </p>
                  {run.notFound.length > 0 && (
                    <p className="text-[12.5px] text-white/45 break-words">Introuvables : {run.notFound.join(', ')}
                      {run.failed > 0 && <> (le service de recherche n'a pas répondu pour {run.failed} d'entre eux, réessaie plus tard)</>}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button icon={running ? Loader2 : Plus} onClick={addAll} disabled={running || picked.length === 0}
              className={cx('w-full sm:w-auto', running && '[&>svg]:animate-spin')}>
              {running ? `${run!.done}/${run!.total}…` : `Tout ajouter (${picked.length})`}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
